import { useEffect, useState } from "react";
import { useDesignContracts } from "../useHook";
import {
  EyeIcon,
  TrashIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import Loader from "../../../Utils/loader";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";
import { UrduInput } from "../../../Components/Shared/UrduInput";

const DESCRIPTION_MAX_WORDS = 100;

const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

function DesignContracts() {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.DesignContract.CREATE);
  const canUpdate = hasPermission(SystemPermissions.DesignContract.UPDATE);
  const canDelete = hasPermission(SystemPermissions.DesignContract.DELETE);
  const canViewMake = hasPermission(SystemPermissions.DesignContract.VIEW_MAKE);
  const canViewUserPdf = hasPermission(
    SystemPermissions.DesignContract.VIEW_USER_PDF,
  );
  const canShowActions =
    canViewMake || canUpdate || canViewUserPdf || canDelete;

  const {
    createDesignContract,
    getDesignContracts,
    deleteDesignContract,
    updateDesignContract,
  } = useDesignContracts();

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [offset, setOffset] = useState(0);
  const [limit] = useState(9);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");

  const [file, setFile] = useState(null);
  const [fileUr, setFileUr] = useState(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [title, setTitle] = useState("");
  const [title_ur, setTitleUr] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [description, setDescription] = useState("");
  const [description_ur, setDescriptionUr] = useState("");

  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editTitleUr, setEditTitleUr] = useState("");
  const [editCustomUrl, setEditCustomUrl] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDescriptionUr, setEditDescriptionUr] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editFileUr, setEditFileUr] = useState(null);
  const [editDocumentUrl, setEditDocumentUrl] = useState(null);
  const [editDocumentName, setEditDocumentName] = useState("");
  const [hasContentUr, setHasContentUr] = useState(false);

  const fetchContracts = async (currentOffset = offset) => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;

      const res = await getDesignContracts(currentOffset, limit, params);
      setContracts(res.data || []);
      setTotal(res.pagination?.totalRecords || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handleSearch = () => {
    setOffset(0);
    fetchContracts(0);
  };

  const handleReset = () => {
    setSearch("");
    setOffset(0);
    fetchContracts(0);
  };

  const resetCreateForm = () => {
    setFile(null);
    setFileUr(null);
    setImage(null);
    setImagePreview(null);
    setTitle("");
    setTitleUr("");
    setCustomUrl("");
    setDescription("");
    setDescriptionUr("");
  };

  const handleUpload = async () => {
    if (!file || !image || !title || !customUrl.trim()) return;
    if (countWords(description) > DESCRIPTION_MAX_WORDS) return;

    const formData = new FormData();
    formData.append("title", title);
    if (title_ur) formData.append("title_ur", title_ur);
    formData.append("description", description);
    if (description_ur) formData.append("description_ur", description_ur);
    formData.append("customUrl", customUrl.trim());
    formData.append("file", file);
    if (fileUr) formData.append("file_ur", fileUr);
    formData.append("image", image);

    try {
      setUploading(true);
      await createDesignContract(formData);
      resetCreateForm();
      setIsModalOpen(false);
      fetchContracts(0);
      Swal.fire("Success", "Design contract uploaded successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err.response?.data?.message || "Upload failed",
        "error",
      );
    } finally {
      setUploading(false);
    }
  };

  const openEditModal = (contract) => {
    setEditId(contract?.id || null);
    setEditTitle(contract?.title || "");
    setEditTitleUr(contract?.title_ur || "");
    setEditCustomUrl(contract?.customUrl || "");
    setEditDescription(contract?.description || "");
    setEditDescriptionUr(contract?.description_ur || "");
    setEditImage(null);
    setEditImagePreview(contract?.image || null);
    setEditFile(null);
    setEditFileUr(null);
    setEditDocumentUrl(contract?.document || null);
    setHasContentUr(!!contract?.content_ur);
    const docUrl = contract?.document || "";
    const rawName = docUrl
      ? decodeURIComponent(docUrl.split("/").pop() || "")
      : "";
    setEditDocumentName(rawName.replace(/^\d+-/, "") || rawName || "");
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editId || !editTitle || !editCustomUrl.trim()) return;
    if (countWords(editDescription) > DESCRIPTION_MAX_WORDS) return;

    try {
      setUpdating(true);
      if (editImage || editFile || editFileUr) {
        const formData = new FormData();
        formData.append("title", editTitle);
        if (editTitleUr) formData.append("title_ur", editTitleUr);
        formData.append("description", editDescription);
        if (editDescriptionUr) formData.append("description_ur", editDescriptionUr);
        formData.append("customUrl", editCustomUrl.trim());
        if (editImage) formData.append("image", editImage);
        if (editFile) formData.append("file", editFile);
        if (editFileUr) formData.append("file_ur", editFileUr);
        await updateDesignContract(editId, formData);
      } else {
        await updateDesignContract(editId, {
          title: editTitle,
          title_ur: editTitleUr,
          description: editDescription,
          description_ur: editDescriptionUr,
          customUrl: editCustomUrl.trim(),
        });
      }
      setIsEditModalOpen(false);
      setEditId(null);
      setEditTitle("");
      setEditTitleUr("");
      setEditCustomUrl("");
      setEditDescription("");
      setEditDescriptionUr("");
      setEditImage(null);
      setEditImagePreview(null);
      setEditFile(null);
      setEditFileUr(null);
      setEditDocumentUrl(null);
      setEditDocumentName("");
      setHasContentUr(false);
      fetchContracts();
      Swal.fire("Success", "Design contract updated successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Update failed", "error");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this design contract!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteDesignContract(id);
        fetchContracts();
        Swal.fire("Deleted!", "Design contract has been deleted.", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Delete failed", "error");
      }
    }
  };

  const handleView = (id) => {
    navigate(`/design-contracts/view/${id}`);
  };

  return (
    <div>
      <div className="bg-gray-100 p-4 flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold">Design Contract Management</h2>

        {canCreate && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn btn-primary h-[44px]"
          >
            Add Design Contract
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search design contracts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <MagnifyingGlassIcon className="search-icon h-5 w-5" />
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={handleSearch} className="btn btn-filter filter-btn">
            <FunnelIcon className="h-4 w-4 text-white" /> Filter
          </button>
          <button onClick={handleReset} className="btn btn-reset-filter filter-btn">
            Reset
          </button>
        </div>
      </div>

      {loading ? (
        <Loader message="Loading design contracts..." fullScreen={false} />
      ) : contracts.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-dashed border-gray-300 text-center text-gray-500">
          No design contracts found matching your criteria.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contracts.map((contract) => (
            <div
              key={contract.id}
              className="bg-white p-5 rounded-2xl shadow-md hover:shadow-lg transition duration-200 border border-gray-100 flex flex-col"
            >
              {contract.image && (
                <div className="mb-3 overflow-hidden rounded-xl border border-gray-100">
                  <img
                    src={contract.image}
                    alt={contract.title || "Design contract"}
                    className="w-full h-40 object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-800 mb-2 truncate">
                  {contract.title}
                </h3>
                {!!contract.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 italic">
                    {contract.description}
                  </p>
                )}
              </div>

              {canShowActions && (
                <div className="flex gap-2 flex-wrap mt-4 pt-4 border-t border-gray-50">
                  {canViewMake && (
                    <button
                      onClick={() => handleView(contract.id)}
                      className="btn btn-view text-white"
                    >
                      <EyeIcon className="h-4 w-4 text-white" />
                      View
                    </button>
                  )}

                  {canUpdate && (
                    <button
                      onClick={() => openEditModal(contract)}
                      className="btn btn-edit text-white"
                    >
                      <PencilSquareIcon className="h-4 w-4 text-white" />
                      Edit
                    </button>
                  )}

                  {canViewUserPdf && (
                    <button
                      onClick={() =>
                        navigate(
                          `/design-contracts/user-contracts/${contract.id}`,
                        )
                      }
                      className="btn btn-action"
                    >
                      <DocumentTextIcon className="h-4 w-4 text-white" />
                      PDFs
                    </button>
                  )}

                  {canDelete && (
                    <button
                      onClick={() => handleDelete(contract.id)}
                      className="btn btn-delete text-white"
                    >
                      <TrashIcon className="h-4 w-4 text-white" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && total > limit && (
        <div className="mt-10">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {canCreate && isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 bg-black/50">
            <div className="bg-white w-full max-w-[500px] p-6 rounded-3xl shadow-2xl relative my-4">
            <button
              onClick={() => {
                setIsModalOpen(false);
                resetCreateForm();
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>

            <h3 className="text-xl font-bold text-gray-800 mb-4 text-center pr-6">
              New Design Contract
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Interior Design Agreement"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
                <UrduInput
                  label="عنوان"
                  name="title_ur"
                  value={title_ur}
                  onChange={(e) => setTitleUr(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Custom URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. interior-design-agreement"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Summary of the design contract..."
                  value={description}
                  onChange={(e) => {
                    const val = e.target.value;
                    const wordMatches = [...val.matchAll(/\S+/g)];
                    if (wordMatches.length <= DESCRIPTION_MAX_WORDS) {
                      setDescription(val);
                    }
                  }}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition min-h-[72px]"
                />
                <div className="flex justify-between mt-1 px-1">
                  <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Word count limit</p>
                  <p className={`text-[10px] font-bold ${countWords(description) >= DESCRIPTION_MAX_WORDS ? "text-red-500" : "text-gray-400"}`}>
                    {countWords(description)}/{DESCRIPTION_MAX_WORDS}
                  </p>
                </div>
                <UrduInput
                  label="تفصیل"
                  name="description_ur"
                  value={description_ur}
                  onChange={(e) => setDescriptionUr(e.target.value)}
                  type="textarea"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Image <span className="text-red-500">*</span>
                </label>
                <label
                  htmlFor="design-contract-image-upload"
                  className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition group"
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-20 object-contain rounded-lg"
                    />
                  ) : (
                    <>
                      <PhotoIcon className="w-7 h-7 text-gray-300 group-hover:text-blue-400 mb-1" />
                      <p className="text-gray-500 text-xs font-medium">
                        Select image
                      </p>
                    </>
                  )}
                  <input
                    id="design-contract-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const selected = e.target.files?.[0] || null;
                      setImage(selected);
                      setImagePreview(
                        selected ? URL.createObjectURL(selected) : null,
                      );
                    }}
                    className="hidden"
                  />
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Upload Word Template (.docx) <span className="text-red-500">*</span>
                </label>
                <label
                  htmlFor="design-contract-file-upload"
                  className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition group"
                >
                  <DocumentTextIcon className="w-7 h-7 text-gray-300 group-hover:text-blue-400 mb-1" />
                  <p className="text-gray-500 text-xs font-medium">
                    {file ? file.name : "Select or drop file"}
                  </p>
                  <input
                    id="design-contract-file-upload"
                    type="file"
                    accept=".doc,.docx"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Upload Urdu Word Template (.docx)
                </label>
                <label
                  htmlFor="design-contract-file-ur-upload"
                  className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition group"
                >
                  <DocumentTextIcon className="w-7 h-7 text-gray-300 group-hover:text-blue-400 mb-1" />
                  <p className="text-gray-500 text-xs font-medium" dir="rtl">
                    {fileUr ? fileUr.name : "اردو ورڈ فائل منتخب کریں"}
                  </p>
                  <input
                    id="design-contract-file-ur-upload"
                    type="file"
                    accept=".doc,.docx"
                    onChange={(e) => setFileUr(e.target.files[0])}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetCreateForm();
                  }}
                  className="btn btn-reset-filter justify-center"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={
                    uploading ||
                    !file ||
                    !image ||
                    !title ||
                    !customUrl.trim()
                  }
                  className={`btn btn-primary justify-center ${
                    uploading ||
                    !file ||
                    !image ||
                    !title ||
                    !customUrl.trim()
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {uploading ? "Uploading..." : "Save"}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {canUpdate && isEditModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 bg-black/50">
            <div className="bg-white w-full max-w-[450px] p-6 rounded-3xl shadow-2xl relative my-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
              Edit Design Contract
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
                <UrduInput
                  label="عنوان"
                  name="edit_title_ur"
                  value={editTitleUr}
                  onChange={(e) => setEditTitleUr(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Custom URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editCustomUrl}
                  onChange={(e) => setEditCustomUrl(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => {
                    const val = e.target.value;
                    const wordMatches = [...val.matchAll(/\S+/g)];
                    if (wordMatches.length <= DESCRIPTION_MAX_WORDS) {
                      setEditDescription(val);
                    }
                  }}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition min-h-[72px]"
                />
                <div className="flex justify-end mt-1">
                  <p className={`text-[10px] font-bold ${countWords(editDescription) >= DESCRIPTION_MAX_WORDS ? "text-red-500" : "text-gray-400"}`}>
                    {countWords(editDescription)}/{DESCRIPTION_MAX_WORDS} words
                  </p>
                </div>
                <UrduInput
                  label="تفصیل"
                  name="edit_description_ur"
                  value={editDescriptionUr}
                  onChange={(e) => setEditDescriptionUr(e.target.value)}
                  type="textarea"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Image
                </label>
                <label
                  htmlFor="edit-design-contract-image"
                  className="flex flex-col items-center justify-center w-full min-h-[96px] border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition group p-3"
                >
                  {editImagePreview ? (
                    <img
                      src={editImagePreview}
                      alt="Current"
                      className="h-24 w-full object-cover rounded-lg"
                    />
                  ) : (
                    <>
                      <PhotoIcon className="w-7 h-7 text-gray-300 group-hover:text-blue-400 mb-1" />
                      <p className="text-gray-500 text-xs font-medium">
                        Click to upload image
                      </p>
                    </>
                  )}
                  {editImage && (
                    <p className="text-[11px] text-green-600 mt-2 font-medium truncate max-w-full">
                      New: {editImage.name}
                    </p>
                  )}
                  {!editImage && editImagePreview && (
                    <p className="text-[11px] text-blue-600 mt-2 font-medium">
                      Click to replace image
                    </p>
                  )}
                  <input
                    id="edit-design-contract-image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const selected = e.target.files?.[0] || null;
                      setEditImage(selected);
                      if (selected) {
                        setEditImagePreview(URL.createObjectURL(selected));
                      }
                    }}
                    className="hidden"
                  />
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Word Template (.docx)
                </label>
                <label
                  htmlFor="edit-design-contract-file"
                  className="flex flex-col items-center justify-center w-full min-h-[96px] border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition group p-3"
                >
                  <DocumentTextIcon className="w-8 h-8 text-blue-400 mb-2" />
                  <p className="text-sm font-medium text-gray-800 text-center truncate max-w-full px-2">
                    {editFile
                      ? editFile.name
                      : editDocumentName || "Click to upload Word file"}
                  </p>
                  {editDocumentUrl && !editFile && (
                    <a
                      href={editDocumentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-blue-600 hover:underline mt-1"
                    >
                      View / Download current file
                    </a>
                  )}
                  {editFile && (
                    <p className="text-[11px] text-green-600 mt-1 font-medium">
                      New file selected — will replace on update
                    </p>
                  )}
                  {!editFile && editDocumentName && (
                    <p className="text-[11px] text-gray-500 mt-1">
                      Click to replace document
                    </p>
                  )}
                  <input
                    id="edit-design-contract-file"
                    type="file"
                    accept=".doc,.docx"
                    onChange={(e) => {
                      setEditFile(e.target.files?.[0] || null);
                    }}
                    className="hidden"
                  />
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Urdu Word Template (.docx)
                </label>
                <label
                  htmlFor="edit-design-contract-file-ur"
                  className="flex flex-col items-center justify-center w-full min-h-[96px] border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition group p-3"
                >
                  <DocumentTextIcon className="w-8 h-8 text-blue-400 mb-2" />
                  <p
                    className="text-sm font-medium text-gray-800 text-center truncate max-w-full px-2"
                    dir="rtl"
                  >
                    {editFileUr
                      ? editFileUr.name
                      : hasContentUr
                        ? "اردو ٹیمپلیٹ موجود ہے — تبدیل کرنے کے لیے کلک کریں"
                        : "اردو ورڈ فائل اپلوڈ کریں"}
                  </p>
                  {editFileUr && (
                    <p className="text-[11px] text-green-600 mt-1 font-medium">
                      New Urdu file selected — will replace on update
                    </p>
                  )}
                  {!editFileUr && hasContentUr && (
                    <p className="text-[11px] text-gray-500 mt-1">
                      Click to replace Urdu document
                    </p>
                  )}
                  <input
                    id="edit-design-contract-file-ur"
                    type="file"
                    accept=".doc,.docx"
                    onChange={(e) => {
                      setEditFileUr(e.target.files?.[0] || null);
                    }}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditFileUr(null);
                  }}
                  disabled={updating}
                  className="btn btn-reset-filter justify-center"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdate}
                  disabled={
                    updating || !editTitle || !editCustomUrl.trim()
                  }
                  className={`btn btn-edit text-white justify-center ${
                    updating || !editTitle || !editCustomUrl.trim()
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {updating ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DesignContracts;
